/* @jsxImportSource react */
'use client';

import { type ComponentPropsWithoutRef, type ReactNode } from 'react';

import { Popover as PopoverBase } from '@base-ui/react/popover';

import { cn } from '../../utils/cn';
import { type PortalContainer, usePortalContainer } from '../../utils/portal-provider';
import { type StripUndesiredProps } from '../../utils/strip-undesired-props';

/** Popover size variant definitions mapping sizes to their surface geometry. */
export const ATOMS_POPOVER_VARIANTS = {
  size: {
    sm: {
      classes: 'rounded-xl p-1.5',
      description: 'Compact popover for dense menus',
    },
    base: {
      classes: 'rounded-2xl p-2',
      description: 'Default popover',
    },
    lg: {
      classes: 'rounded-3xl p-3',
      description: 'Spacious popover for rich content',
    },
  },
} as const;

export const ATOMS_POPOVER_DEFAULT_VARIANTS = {
  size: 'base',
} as const;

export type AtomsPopoverSize = keyof typeof ATOMS_POPOVER_VARIANTS.size;

export interface AtomsPopoverVariantsProps {
  /**
   * Popover surface geometry.
   * - `"sm"` — Compact for dense menus
   * - `"base"` — Default
   * - `"lg"` — Spacious for rich content
   * @default "base"
   */
  size?: AtomsPopoverSize;
}

export function popoverVariants({
  size = ATOMS_POPOVER_DEFAULT_VARIANTS.size,
}: AtomsPopoverVariantsProps = {}) {
  return cn(
    // Surface: matches the combobox popup (primary background, 2px brand ring,
    // soft shadow) so anchored popups read as one family. The popup receives
    // focus on open, so the native outline is suppressed in favor of the ring.
    'bg-od-bg-primary text-od-content-primary shadow-lg outline-none ring-2 ring-od-border-focused-accent-brand',
    // Constrain to the space the positioner computed and scroll overflow.
    'max-h-(--available-height) max-w-(--available-width) overflow-y-auto overscroll-contain',
    // Scale + fade from the anchor edge on open/close.
    'origin-(--transform-origin) transition-[transform,scale,opacity] duration-150',
    'data-starting-style:scale-95 data-starting-style:opacity-0 data-ending-style:scale-95 data-ending-style:opacity-0',
    ATOMS_POPOVER_VARIANTS.size[size].classes,
  );
}

type PositionerProps = ComponentPropsWithoutRef<typeof PopoverBase.Positioner>;
type PopupProps = ComponentPropsWithoutRef<typeof PopoverBase.Popup>;

/**
 * Popover component props — the anchored content panel.
 *
 * @example
 * ```tsx
 * <Popover.Root>
 *   <Popover.Trigger render={(p) => <Button aria-label="Open" analyticsName="app-open" {...p}>Open</Button>} />
 *   <Popover align="start">
 *     <MenuContent />
 *   </Popover>
 * </Popover.Root>
 * ```
 */
export type PopoverProps = AtomsPopoverVariantsProps & {
  /** Additional CSS classes merged via `cn()`. Use atoms semantic tokens only. */
  className?: string;
  /** Popover content. */
  children: ReactNode;
  /**
   * Which side of the anchor to place the popover on.
   * @default "bottom"
   */
  side?: PositionerProps['side'];
  /**
   * Alignment of the popover relative to the anchor.
   * @default "center"
   */
  align?: PositionerProps['align'];
  /**
   * Distance in px between the anchor and the popover.
   * @default 8
   */
  sideOffset?: PositionerProps['sideOffset'];
  /**
   * Anchor element (or ref/getter) to position against. Defaults to the
   * `Popover.Trigger` in the same `Popover.Root`.
   */
  anchor?: PositionerProps['anchor'];
  /** Determines the element to focus when the popover is opened. */
  initialFocus?: PopupProps['initialFocus'];
  /** Determines the element to focus when the popover is closed. */
  finalFocus?: PopupProps['finalFocus'];
  /**
   * Container element for the portal. Use this to render the popover inside
   * a Shadow DOM or custom container. Overrides `AtomsPortalProvider` context.
   * @default document.body (or AtomsPortalProvider container if set)
   */
  container?: PortalContainer;
};

/**
 * Anchored popover panel. Compound component with `Popover.Root`,
 * `Popover.Trigger`, `Popover.Title`, `Popover.Description`, and
 * `Popover.Close`. Non-modal by default: light-dismisses on outside click and
 * Escape, and returns focus to the trigger.
 *
 * Use `Popover` for anchored surfaces such as switchers, quick pickers, and
 * contextual detail panels; use `Dialog` when the interaction should block the
 * page behind a scrim.
 *
 * @example
 * ```tsx
 * <Popover.Root>
 *   <Popover.Trigger render={(p) => <Button aria-label="Saved items" analyticsName="app-saved-items" {...p}>Saved items</Button>} />
 *   <Popover align="start" className="w-80">
 *     <Popover.Title>Saved items</Popover.Title>
 *     <ItemList />
 *   </Popover>
 * </Popover.Root>
 * ```
 */
function PopoverContent({
  className,
  children,
  size = ATOMS_POPOVER_DEFAULT_VARIANTS.size,
  side = 'bottom',
  align = 'center',
  sideOffset = 8,
  anchor,
  initialFocus,
  finalFocus,
  container: containerProp,
}: PopoverProps) {
  const contextContainer = usePortalContainer();
  const container = containerProp ?? contextContainer ?? undefined;

  return (
    <PopoverBase.Portal container={container}>
      <PopoverBase.Positioner
        side={side}
        align={align}
        sideOffset={sideOffset}
        anchor={anchor}
        className="outline-none"
      >
        <PopoverBase.Popup
          className={cn(popoverVariants({ size }), className)}
          initialFocus={initialFocus}
          finalFocus={finalFocus}
        >
          {children}
        </PopoverBase.Popup>
      </PopoverBase.Positioner>
    </PopoverBase.Portal>
  );
}

// ============================================================================
// Popover Root
// ============================================================================

export type PopoverRootProps = Omit<ComponentPropsWithoutRef<typeof PopoverBase.Root>, 'style'>;

function PopoverRoot(props: PopoverRootProps) {
  return <PopoverBase.Root {...props} />;
}

PopoverRoot.displayName = 'Popover.Root';

// ============================================================================
// Popover Trigger
// ============================================================================

export type PopoverTriggerProps = Omit<
  ComponentPropsWithoutRef<typeof PopoverBase.Trigger>,
  'handle' | 'style'
>;

function PopoverTrigger({ children, ...props }: PopoverTriggerProps) {
  return <PopoverBase.Trigger {...props}>{children}</PopoverBase.Trigger>;
}

PopoverTrigger.displayName = 'Popover.Trigger';

// ============================================================================
// Popover Title
// ============================================================================

export type PopoverTitleProps = StripUndesiredProps<
  ComponentPropsWithoutRef<typeof PopoverBase.Title>
>;

function PopoverTitle({ className, ...props }: PopoverTitleProps) {
  return (
    <PopoverBase.Title
      className={cn('text-label-md text-od-content-primary', className)}
      {...props}
    />
  );
}

PopoverTitle.displayName = 'Popover.Title';

// ============================================================================
// Popover Description
// ============================================================================

export type PopoverDescriptionProps = StripUndesiredProps<
  ComponentPropsWithoutRef<typeof PopoverBase.Description>
>;

function PopoverDescription({ className, ...props }: PopoverDescriptionProps) {
  return (
    <PopoverBase.Description
      className={cn('text-body-sm text-od-content-secondary', className)}
      {...props}
    />
  );
}

PopoverDescription.displayName = 'Popover.Description';

// ============================================================================
// Popover Close
// ============================================================================

export type PopoverCloseProps = StripUndesiredProps<
  ComponentPropsWithoutRef<typeof PopoverBase.Close>
>;

function PopoverClose({ children, ...props }: PopoverCloseProps) {
  return <PopoverBase.Close {...props}>{children}</PopoverBase.Close>;
}

PopoverClose.displayName = 'Popover.Close';

// ============================================================================
// Compound Component Export
// ============================================================================

const Popover = Object.assign(PopoverContent, {
  Root: PopoverRoot,
  Trigger: PopoverTrigger,
  Title: PopoverTitle,
  Description: PopoverDescription,
  Close: PopoverClose,
});

export { Popover, PopoverRoot, PopoverTrigger, PopoverTitle, PopoverDescription, PopoverClose };
